import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaTimes, FaSpinner } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { NumericFormat } from 'react-number-format';

interface FormData {
  productCode: string;
  name: string;
  categoryId: string;
  uomId: string;
  defaultLocationId: string;
  customerProductCode: string;
  costValue: number;
  procurementLeadTime: number;
}

interface Category {
  id: string;
  name: string;
}

interface Uom {
  id: string;
  name: string;
}

interface Location {
  id: string;
  name: string;
}

const ProductsForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;
  const {logout} = useAuth();

  const [formData, setFormData] = useState<FormData>({
    productCode: "",
    name: "",
    categoryId: "",
    uomId: "",
    defaultLocationId: "",
    customerProductCode: "",
	costValue: 0,
	procurementLeadTime: 0,
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchData = async () => {
    try {
      const [catRes, uomRes, locRes] = await Promise.all([
        axios.get(`${API}/categories`, getAuthHeader()),
        axios.get(`${API}/uoms`, getAuthHeader()),
        axios.get(`${API}/locations`, getAuthHeader()),
      ]);

      setCategories(catRes.data.data || []);
	  setUoms(uomRes.data.data || []);
	  setLocations(locRes.data.data || []);

      if (isEdit) {
        const res = await axios.get(`${API}/products/${id}`, getAuthHeader());
        const data = res.data;
        setFormData({
          productCode: data.product_code || "",
          name: data.name || "",
          categoryId: data.category_id || "",
          uomId: data.uom_id || "",
          defaultLocationId: data.default_location_id || "",
          customerProductCode: data.customer_product_code || "",
		  costValue: data.cost_value || 0,
		  procurementLeadTime: data.procurement_lead_time || 0,
        });
      }
    } catch (err:any) {
      //console.error("Failed to fetch data", error);
	  if (err.response?.status === 401) {
		  logout();
		  //localStorage.removeItem("token"); // opsional: hapus token biar nggak infinite loop
		  //localStorage.removeItem("user");
		  navigate("/login"); // redirect ke login
		}
      alert("Error fetching data. Please try again.");
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    const { productCode, name } = formData;
    if (!productCode || !name) {
      alert("Please fill all mandatory fields.");
      return;
    }

    const confirmSave = window.confirm("Do you want to save this product?");
    if (!confirmSave) return;

    setLoading(true);

    try {
      const rawPayload = {
        ...formData,
        tenantId: user.tenantId,
        createdByUserId: user.id,
		locationId: formData.defaultLocationId, // ✅ mapping ulang
      };

	  //delete rawPayload.defaultLocationId; // ✅ optional: agar tidak terkirim dobel
      
	  const payload = Object.fromEntries(
        Object.entries(rawPayload).filter(([_, v]) => v !== "")
      );
	  
	  //console.error("payload:", payload);
	  //console.log(payload);
      if (isEdit) {
        await axios.put(`${API}/products/${id}`, payload, getAuthHeader());
        alert("Product updated!");
      } else {
        await axios.post(`${API}/products`, payload, getAuthHeader());
        alert("Product created!");
      }

      navigate("/products");
    } catch (error: any) {
      console.error("Failed to save product:", error);
      const message =
        error?.response?.data?.message || "Failed to save product";
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this product?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/products/${id}`, getAuthHeader());
      alert("Product deleted");
      navigate("/products");
    } catch (error) {
      console.error("Failed to delete product:", error);
      alert("Failed to delete product");
    }
  };
  
  //additional process
  const formatNumber = (value: string | number) => {
	  const num = Number(value);
	  return isNaN(num) ? "0" : num.toLocaleString("id-ID");
	};

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-700">
            {isEdit ? "Edit Product" : "Add Product"}
          </h2>
          <div className="flex gap-2">
            <button
              type="submit"
              form="product-form"
              className="bg-blue-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-2 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Save
            </button>
            <button
              onClick={() => navigate("/products")}
              className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-[13px] flex items-center gap-2"
            >
              <FaTimes />
              Cancel
            </button>
            {isEdit && (
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-2"
                disabled={loading}
              >
                <FaTimes />
                Delete
              </button>
            )}
          </div>
        </div>

        <form id="product-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="flex items-center">
                <label className="w-32 text-[13px] font-medium">
                  Product Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="productCode"
                  value={formData.productCode}
                  onChange={handleChange}
                  required
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                />
              </div>

              <div className="flex items-center">
                <label className="w-32 text-[13px] font-medium">
				  Name  <span className="text-red-500">*</span>
				</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
				  required
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                />
              </div>
			  <div className="flex items-center">
                <label className="w-32 text-[13px] font-medium">Category</label>
                <select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
			  
			  <div className="flex items-center">
                <label className="w-32 text-[13px] font-medium">Proc. LT (days)</label>
				<NumericFormat
				  value={formData.procurementLeadTime}
				  thousandSeparator="."
				  decimalSeparator=","
				  displayType="input"
				  allowNegative={false}
				  allowLeadingZeros={false}
				  onValueChange={(values) => {
				  const { floatValue } = values;
				  setFormData((prev) => ({
					...prev,
					procurementLeadTime: floatValue ?? 0,
				  }));
				}}
				  className="flex-1 border rounded px-2 py-1 text-[13px] text-right"
				/>
              </div>
              
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              

              <div className="flex items-center">
                <label className="w-32 text-[13px] font-medium">UOM</label>
                <select
                  name="uomId"
                  value={formData.uomId}
                  onChange={handleChange}
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                >
                  <option value="">-- Select UOM --</option>
                  {uoms.map((uom) => (
                    <option key={uom.id} value={uom.id}>
                      {uom.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <label className="w-32 text-[13px] font-medium">Default Location</label>
                <select
                  name="defaultLocationId"
                  value={formData.defaultLocationId}
                  onChange={handleChange}
                  className="flex-1 border rounded px-2 py-1 text-[13px]"
                >
                  <option value="">-- Select Default Location --</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
			  
			  <div className="flex items-center">
                <label className="w-32 text-[13px] font-medium">Cost Value</label>
				<NumericFormat
				  value={formData.costValue}
				  thousandSeparator="."
				  decimalSeparator=","
				  displayType="input"
				  allowNegative={false}
				  allowLeadingZeros={false}
				  onValueChange={(values) => {
				  const { floatValue } = values;
				  setFormData((prev) => ({
					...prev,
					costValue: floatValue ?? 0,
				  }));
				}}
				  className="flex-1 border rounded px-2 py-1 text-[13px] text-right"
				/>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductsForm;
